import React from 'react';
import { Link } from 'react-router-dom';
import { Badge, Card, Empty, Segmented, Table, Tabs, Tag, Typography } from 'antd';
import { ClockCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  PendingApproval,
  usePendingApprovalCount,
  usePendingApprovals,
} from '../../hooks/useApprovalHooks';

const { Text } = Typography;

/**
 * Where each module's documents are read.
 *
 * The queue is deliberately a list of links rather than a place to approve
 * from: the decision belongs on the document, next to the prices, quantities
 * and history the approver is deciding on.
 */
const DETAIL_ROUTE: Record<string, string> = {
  PO: '/purchase-order',
  PR: '/purchase-request',
  CANVASS: '/purchase-canvass',
  BOM: '/production/bill-of-material',
  RR: '/stock-movement/receiving-report',
  DR: '/stock-movement/delivery-receipt',
  MR: '/stock-movement/material-request',
};

const MODULE_LABEL: Record<string, string> = {
  PO: 'Purchase Order',
  PR: 'Purchase Request',
  CANVASS: 'Canvass',
  BOM: 'Bill of Material',
  RR: 'Receiving Report',
  DR: 'Internal DR',
  MR: 'Material Request',
};

const MODULE_COLOUR: Record<string, string> = {
  PO: 'blue',
  PR: 'geekblue',
  CANVASS: 'purple',
  BOM: 'cyan',
  RR: 'green',
  DR: 'orange',
  MR: 'gold',
};

/**
 * The tabs, in the order the flow reaches them.
 *
 * Fixed rather than derived from whatever happens to be in the queue: a module
 * with nothing waiting keeps its tab and shows a zero, which answers "is there
 * anything for me on DR?" without the user having to wonder where the tab went.
 *
 * BOM is absent on purpose -- 7,811 of them rest at CONFIRM and none is waiting
 * on anyone, so it has never belonged in this queue.
 */
const TABS = ['PR', 'CANVASS', 'PO', 'MR', 'RR', 'DR'] as const;

const ApprovalsPage: React.FC = () => {
  // This year is the working view. Everything sent before it is a backlog to
  // clear rather than something to hide, so it is one click away and carries
  // its own count.
  const [includeEarlier, setIncludeEarlier] = React.useState(false);
  const { data, isLoading } = usePendingApprovals(includeEarlier);
  const { data: counts } = usePendingApprovalCount();
  const [tab, setTab] = React.useState<string>('ALL');
  const all = data ?? [];
  const rows = tab === 'ALL' ? all : all.filter((r) => r.module === tab);
  const earlier = counts?.earlier ?? 0;
  const thisYear = new Date().getFullYear();

  const countFor = (m: string) =>
    m === 'ALL' ? all.length : all.filter((r) => r.module === m).length;

  return (
    <Card
      bordered={false}
      title={
        <span>
          <ClockCircleOutlined style={{ marginRight: 8 }} />
          Waiting for your approval
          {rows.length > 0 && (
            <Tag color="red" style={{ marginLeft: 8 }}>
              {rows.length}
            </Tag>
          )}
        </span>
      }
      extra={
        earlier > 0 || includeEarlier ? (
          <Segmented
            value={includeEarlier ? 'all' : 'year'}
            onChange={(v) => setIncludeEarlier(v === 'all')}
            options={[
              { label: `${thisYear}`, value: 'year' },
              { label: `Including earlier (${earlier})`, value: 'all' },
            ]}
          />
        ) : null
      }
    >
      <Tabs
        activeKey={tab}
        onChange={setTab}
        items={['ALL', ...TABS].map((m) => ({
          key: m,
          label: (
            <span>
              {m === 'ALL' ? 'All' : MODULE_LABEL[m] ?? m}
              <Badge
                count={countFor(m)}
                showZero
                style={{
                  marginLeft: 8,
                  backgroundColor: countFor(m) > 0 ? '#cf1322' : '#d9d9d9',
                }}
              />
            </span>
          ),
        }))}
      />

      {!isLoading && rows.length === 0 ? (
        <Empty
          description={
            tab !== 'ALL' && all.length > 0
              ? `No ${MODULE_LABEL[tab] ?? tab} waiting. ${all.length} other ${
                  all.length === 1 ? 'document is' : 'documents are'
                } in the queue.`
              : earlier > 0 && !includeEarlier
                ? `Nothing sent to you in ${thisYear}. ${earlier} older ${
                    earlier === 1 ? 'document is' : 'documents are'
                  } still waiting.`
                : 'Nothing is waiting on you.'
          }
        />
      ) : (
        <Table<PendingApproval>
          rowKey={(r) => `${r.module}-${r.id}`}
          dataSource={rows}
          loading={isLoading}
          pagination={{ showSizeChanger: true, pageSizeOptions: ['10', '20', '50'] }}
        >
          <Table.Column
            title="Document"
            dataIndex="code"
            render={(_t, r: PendingApproval) => (
              <Link to={`${DETAIL_ROUTE[r.module] ?? ''}/${r.id}`}>
                {r.code || `#${r.id}`}
              </Link>
            )}
          />
          <Table.Column
            title="Type"
            dataIndex="module"
            render={(_t, r: PendingApproval) => (
              <Tag color={MODULE_COLOUR[r.module] ?? 'default'}>
                {MODULE_LABEL[r.module] ?? r.module}
              </Tag>
            )}
          />
          <Table.Column
            title="Submitted by"
            dataIndex="submittedBy"
            render={(_t, r: PendingApproval) =>
              // The queue is built on the submission, so this is always set;
              // the fallback is here so a missing name never renders as a gap.
              r.submittedBy ?? <Text type="secondary">not recorded</Text>
            }
          />
          <Table.Column
            title="Waiting since"
            dataIndex="submittedAt"
            render={(_t, r: PendingApproval) => {
              if (!r.submittedAt) return <Text type="secondary">—</Text>;
              const sent = dayjs(r.submittedAt);
              const days = dayjs().startOf('day').diff(sent.startOf('day'), 'day');
              return (
                <span>
                  {sent.format('D MMM YYYY')}{' '}
                  <Text type={days >= 3 ? 'danger' : 'secondary'}>
                    {days === 0
                      ? '(today)'
                      : `(${days} day${days === 1 ? '' : 's'})`}
                  </Text>
                </span>
              );
            }}
          />
        </Table>
      )}
    </Card>
  );
};

export default ApprovalsPage;
